import { NavType } from '@enums/NavType';
import { useRouterService } from '@hooks/common/UseRouterService';

export const useSideNav = (currentUrl: string): SideNavType => {
  const currentHomePage = localStorage.getItem('homePageOptionUrl');
  const lastNavigation = JSON.parse(localStorage.getItem('lastNavigation'));
  const {
    goToEUIDashboard,
    goToPortfolio,
    goToServiceConquest,
    goToAcquire,
    goToDigitalAudience,
    goToReporting,
    goToAdminPanel,
    goToPrivateIncentiveLookup,
    goToMarketImpact,
    goToMarketImpactSummary,
    goToTurn,
    goToRecent,
    goToFavorites,
    goToYourProfile,
    goToSupport,
    goToGetMobileApp,
  } = useRouterService();

  const isActive = (name: string): boolean => {
    // don't highlight admin when we click on reports (reports url starts with admin/)
    if (
      name === 'admin' &&
      (currentUrl.indexOf('reports') > -1 || currentUrl.indexOf('discounts') > -1)
    ) {
      return false;
    }

    return currentUrl.indexOf(name) > -1;
  };

  const buildLoyaltyUrl = (currentHomePage: string): string => {
    let loyaltyUrl = currentHomePage;
    if (
      loyaltyUrl &&
      !(
        loyaltyUrl.includes('lease') ||
        loyaltyUrl.includes('finance') ||
        loyaltyUrl.includes('cash')
      )
    ) {
      loyaltyUrl = '/portfolio/all';
    }

    return loyaltyUrl;
  };

  const isActiveTab = (link: string): boolean => {
    switch (link) {
      case NavType.Portfolio:
        return isActive('dealsheet') || isActive('/essential/dashboard');

      case NavType.Loyalty:
        const loyaltyUrl = buildLoyaltyUrl(currentHomePage);
        const type = typeof loyaltyUrl === 'string';
        const isLoyalty = type ? loyaltyUrl.includes('portfolio') : false;
        const isService = type ? lastNavigation?.state === 'app.service-conquest' : false;
        const isTurn = type ? lastNavigation?.state === 'app.turn.details' : false;

        return (
          !isService &&
          !isTurn &&
          isLoyalty &&
          (isActive('all') || isActive('lease') || isActive('finance') || isActive('cash'))
        );

      case NavType.ServiceToSales:
        return isActive('service-to-sales');

      case NavType.Service:
        const isServiceActive = isActive('service') && !isActive('service-portfolio');
        return (
          isServiceActive ||
          (isActive('dealsheet') && lastNavigation?.state === 'app.service-conquest')
        );

      case NavType.Acquire:
        return isActive('acquire');

      case NavType.DigitalAudience:
        return isActive('digital-audience');

      case NavType.Reporting:
      case NavType.ReportingPrivateIncentive:
        return isActive('reporting');

      case NavType.StoreSettings:
        return isActive('admin');

      case NavType.PrivateIncentiveLookup:
        return isActive('privateincentivelookup');

      case NavType.MarketImpact:
      case NavType.MarketImpactNew:
      case NavType.MarketImpactUsed:
        return (
          isActive('market-impact') ||
          isActive('market-impact/new') ||
          isActive('market-impact/used')
        );
      case NavType.Turn:
        return (
          isActive('turn') ||
          (currentUrl.indexOf('/dealsheet') > -1 && lastNavigation?.state === 'app.turn.details')
        );
      case NavType.MarketImpactSummary:
        return isActive('market-impact-summary');

      case NavType.Recent:
        return isActive('recent');

      case NavType.Favorites:
        return isActive('favorites');

      case NavType.YourProfile:
        return isActive('your-profile');

      case NavType.Support:
        return isActive('support');
    }
  };

  const redirectToUrl = (link: string): void => {
    switch (link) {
      case NavType.Portfolio:
        goToEUIDashboard();
        break;

      case NavType.Loyalty:
        const loyaltyUrl = buildLoyaltyUrl(currentHomePage);
        const dealType = loyaltyUrl.split('/')[2];
        goToPortfolio(dealType);
        break;

      case NavType.Service:
        goToServiceConquest();
        break;

      case NavType.Acquire:
        goToAcquire();
        break;

      case NavType.DigitalAudience:
        goToDigitalAudience();
        break;

      case NavType.Reporting:
        goToReporting('performanceInsights');
        break;

      case NavType.ReportingPrivateIncentive:
        goToReporting('privateIncentiveInsights');
        break;

      case NavType.StoreSettings:
        goToAdminPanel(!isActive('admin'));
        break;

      case NavType.PrivateIncentiveLookup:
        goToPrivateIncentiveLookup();
        break;

      case NavType.MarketImpactNew:
        goToMarketImpact('new');
        break;

      case NavType.MarketImpactUsed:
        goToMarketImpact('used');
        break;

      case NavType.Turn:
        goToTurn();
        break;

      case NavType.MarketImpactSummary:
        goToMarketImpactSummary();
        break;

      case NavType.Recent:
        goToRecent();
        break;

      case NavType.Favorites:
        goToFavorites();
        break;

      case NavType.YourProfile:
        goToYourProfile();
        break;

      case NavType.Support:
        goToSupport();
        break;

      case NavType.GetMobileApp:
        goToGetMobileApp(false);
        break;
    }
  };

  return { isActive, isActiveTab, buildLoyaltyUrl, redirectToUrl };
};

type SideNavType = {
  isActive(name: string): boolean;
  isActiveTab(link: string): boolean;
  buildLoyaltyUrl(currentHomePage: string): string;
  redirectToUrl(link: string): void;
};
